import { useMemo, useState, type ReactElement, type Ref } from 'react';
import { Link2, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type { RelatedCard } from '@molt/shared';

import { Button, EmptyState, IconButton, Notice, Popover, Skeleton } from '@/components';
import { announce } from '@/lib/announce';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';
import {
  getCardAddressByShortLink,
  relatedCardsQueryOptions,
  useCreateCardRelation,
  useDeleteCardRelation,
} from '@/lib/related-cards';

/**
 * FE-80.1. The two module-scope helpers below take a translator as a REQUIRED
 * first parameter rather than reading the context, on `CardVoteSection`'s rule:
 * a non-component function cannot call a hook, and a *required* leading
 * parameter makes a missed call site a `tsc` error instead of an English
 * literal that renders forever.
 *
 * ⚠ `resolveTargetCardId` and `addRelation` are deliberately NOT given this
 * treatment. They are closures over component state (`selectedCardId`,
 * `pastedCard`, `existingIds`) and cannot be lifted out, so they read `t` from
 * component scope. Inventing a parameter for them to match the precedent would
 * buy no `tsc` coverage that the closure does not already have.
 */
type InterfaceTranslator = InterfaceLanguageContextValue['t'];

export interface RelatedCardOption {
  readonly id: string;
  readonly title: string;
  readonly boardTitle?: string | undefined;
  readonly listTitle: string;
}

export interface RelatedCardsSectionProps {
  readonly cardId: string;
  readonly boardId: string;
  readonly canEdit: boolean;
  /**
   * Render ONLY the trigger control, for the card back's "Add to card" row
   * (`docs/design/stage2/card-back.md` D1): no heading, no empty-state prose.
   * The trigger keeps its accessible name and its popover is unchanged. A
   * viewer who cannot edit gets nothing, because an empty section with no
   * control in it says nothing the card back needs to say.
   */
  readonly compact?: boolean | undefined;
  /**
   * The trigger, so the card back can focus it when this section first moves
   * into the body because the member just filled it from its compact trigger
   * in the "Add to card" row, which unmounted with focus inside it.
   */
  readonly triggerRef?: Ref<HTMLButtonElement> | undefined;
  readonly cardOptions: readonly RelatedCardOption[];
}

/**
 * The row's title: the card's own for a target the viewer can see, and one
 * routed generic sentence for one they cannot. `FR-COLLAB-005` AC — the
 * inaccessible variant carries an id and a reciprocity flag and nothing else,
 * so there is no title to fall back to.
 */
function relatedCardTitle(t: InterfaceTranslator, card: RelatedCard): string {
  return card.accessible ? card.title : t('cardBack.relatedCards.inaccessibleTitle');
}

/**
 * The muted meta line, as ONE whole catalogue value per line the section can
 * render — see the `cardBack.relatedCards.meta.*` comment for why the arm count
 * is six and not two-plus-a-slot.
 *
 * ⚠ The inaccessible arms return the reciprocity line ALONE. Before FE-80.1 the
 * JSX appended a reciprocity suffix to whatever this ternary produced, which on
 * the inaccessible arm rendered it twice — `Connected both ways / connected
 * both ways`, measured. Folding the suffix into these six values is what
 * deletes the repeat; do not reintroduce a suffix at the call site.
 */
function relatedCardMeta(t: InterfaceTranslator, card: RelatedCard): string {
  if (!card.accessible) {
    return card.reciprocal
      ? t('cardBack.relatedCards.meta.inaccessibleBothWays')
      : t('cardBack.relatedCards.meta.inaccessibleOneWay');
  }

  const values = { board: card.boardTitle, list: card.list.title };
  if (card.archived) {
    return card.reciprocal
      ? t('cardBack.relatedCards.meta.archivedBothWays', values)
      : t('cardBack.relatedCards.meta.archivedOneWay', values);
  }
  return card.reciprocal
    ? t('cardBack.relatedCards.meta.bothWays', values)
    : t('cardBack.relatedCards.meta.oneWay', values);
}

export function RelatedCardsSection({
  cardId,
  boardId,
  canEdit,
  compact = false,
  triggerRef = null,
  cardOptions,
}: RelatedCardsSectionProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  const related = useQuery(relatedCardsQueryOptions(cardId));
  const createRelation = useCreateCardRelation();
  const deleteRelation = useDeleteCardRelation();
  const [open, setOpen] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState('');
  const [pastedCard, setPastedCard] = useState('');
  const [reciprocal, setReciprocal] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [targetError, setTargetError] = useState<string | null>(null);
  const [resolvingTarget, setResolvingTarget] = useState(false);
  const existingIds = useMemo(
    () => new Set((related.data ?? []).map((card) => card.id)),
    [related.data],
  );
  const options = cardOptions.filter((option) => !existingIds.has(option.id));

  function extractCardShortLink(value: string): string | null {
    const trimmed = value.trim();
    if (trimmed === '') return null;

    try {
      const base =
        typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
      const url = new URL(trimmed, base);
      const [, prefix, shortLink] = url.pathname.split('/');
      if (prefix === 'c' && shortLink !== undefined && shortLink !== '') {
        return decodeURIComponent(shortLink);
      }
    } catch {
      const match = /\/c\/([^/?#]+)/.exec(trimmed);
      if (match?.[1] !== undefined) return decodeURIComponent(match[1]);
    }

    return trimmed.includes('/') || trimmed.includes(' ') ? null : trimmed;
  }

  async function resolveTargetCardId(): Promise<string | null> {
    if (selectedCardId !== '') return selectedCardId;

    const shortLink = extractCardShortLink(pastedCard);
    if (shortLink === null) {
      setTargetError(t('cardBack.relatedCards.error.invalidLink'));
      return null;
    }

    setResolvingTarget(true);
    try {
      const target = await getCardAddressByShortLink(shortLink);
      if (target.id === cardId) {
        setTargetError(t('cardBack.relatedCards.error.self'));
        return null;
      }
      if (existingIds.has(target.id)) {
        setTargetError(t('cardBack.relatedCards.error.duplicate'));
        return null;
      }
      return target.id;
    } catch {
      setTargetError(t('cardBack.relatedCards.error.notFound'));
      return null;
    } finally {
      setResolvingTarget(false);
    }
  }

  async function addRelation() {
    setTargetError(null);
    const toCardId = await resolveTargetCardId();
    if (toCardId === null) return;

    createRelation.mutate(
      { cardId, boardId, body: { toCardId, reciprocal } },
      {
        onSuccess: () => {
          setSelectedCardId('');
          setPastedCard('');
          setOpen(false);
          announce(
            reciprocal
              ? t('cardBack.relatedCards.announce.connected')
              : t('cardBack.relatedCards.announce.added'),
          );
        },
      },
    );
  }

  const editor = canEdit ? (
    <Popover
      open={open}
      onOpenChange={setOpen}
      title={t('cardBack.relatedCards.pickerTitle')}
      width={360}
      trigger={
        compact ? (
          // The "Add to card" row's shape: a labelled secondary button rather
          // than a bare icon, with the SAME accessible name as the icon below.
          <Button
            ref={triggerRef}
            type="button"
            size="sm"
            variant="secondary"
            iconStart={<Link2 aria-hidden="true" className="size-4" />}
          >
            {t('cardBack.relatedCards.addLabel')}
          </Button>
        ) : (
          <IconButton
            ref={triggerRef}
            label={t('cardBack.relatedCards.addLabel')}
            size="sm"
          >
            <Link2 aria-hidden="true" className="size-4" />
          </IconButton>
        )
      }
      closeControl={
        <IconButton label={t('cardBack.relatedCards.closePicker')} size="sm">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm font-medium text-ink">
          {t('cardBack.relatedCards.boardCardLabel')}
          <select
            className="rounded-card border border-border bg-surface px-2 py-1.5 text-sm text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
            value={selectedCardId}
            onChange={(event) => {
              setSelectedCardId(event.currentTarget.value);
              setTargetError(null);
            }}
          >
            <option value="">{t('cardBack.relatedCards.chooseCard')}</option>
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.title}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm font-medium text-ink">
          {t('cardBack.relatedCards.urlLabel')}
          <input
            className="rounded-card border border-border bg-surface px-2 py-1.5 text-sm text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
            value={pastedCard}
            placeholder={t('cardBack.relatedCards.urlPlaceholder')}
            onChange={(event) => {
              setPastedCard(event.currentTarget.value);
              setSelectedCardId('');
              setTargetError(null);
            }}
          />
        </label>
        {targetError === null ? null : (
          <Notice
            tone="bad"
            title={targetError}
            body={t('cardBack.relatedCards.error.body')}
          />
        )}
        <label className="flex items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={reciprocal}
            onChange={(event) => setReciprocal(event.currentTarget.checked)}
          />
          {t('cardBack.relatedCards.reciprocalLabel')}
        </label>
        <Button
          size="sm"
          variant="primary"
          busy={createRelation.isPending || resolvingTarget}
          disabled={selectedCardId === '' && pastedCard.trim() === ''}
          onClick={() => void addRelation()}
        >
          {t('cardBack.relatedCards.submit')}
        </Button>
      </div>
    </Popover>
  ) : null;

  if (compact) return editor;

  return (
    <section aria-labelledby="related-cards-heading" className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <h2 id="related-cards-heading" className="text-sm font-semibold text-ink">
          {t('cardBack.relatedCards.heading')}
        </h2>
        <div className="min-w-0 flex-1" />
        {editor}
      </div>

      {notice === null ? null : (
        <Notice
          tone="info"
          title={notice}
          body={t('cardBack.relatedCards.notice.body')}
        />
      )}

      {related.isLoading ? (
        <Skeleton shape="block" count={2} />
      ) : related.isError ? (
        <Notice
          tone="bad"
          title={t('cardBack.relatedCards.loadFailedTitle')}
          body={t('cardBack.relatedCards.loadFailedBody')}
        />
      ) : related.data === undefined || related.data.length === 0 ? (
        <EmptyState
          tone="neutral"
          size="inline"
          title={t('cardBack.relatedCards.empty')}
        />
      ) : (
        <ul className="flex flex-col gap-2" aria-label={t('cardBack.relatedCards.list')}>
          {related.data.map((card) => {
            const title = relatedCardTitle(t, card);
            return (
              <li
                key={card.id}
                className="flex min-w-0 items-center gap-2 rounded-card border border-border bg-surface-raised px-2 py-1.5 text-sm"
              >
                <Link2 aria-hidden="true" className="size-4 shrink-0 text-ink-muted" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-ink">{title}</span>
                  <span className="block truncate text-xs text-ink-muted">
                    {relatedCardMeta(t, card)}
                  </span>
                </span>
                {canEdit ? (
                  <Button
                    size="sm"
                    variant="quiet"
                    busy={deleteRelation.isPending}
                    aria-label={t('cardBack.relatedCards.removeLabel', { title })}
                    onClick={() => {
                      deleteRelation.mutate(
                        { cardId, boardId, toCardId: card.id },
                        {
                          onSuccess: () => {
                            setNotice(
                              card.reciprocal
                                ? t('cardBack.relatedCards.notice.removedOneSide')
                                : t('cardBack.relatedCards.notice.removed'),
                            );
                          },
                        },
                      );
                    }}
                  >
                    {t('cardBack.relatedCards.remove')}
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
